/* eslint-disable no-param-reassign -- animates the elements it's given through their style */
// Ported from the onboarding prototype: button-driven shared elements across
// the three scenes. All selectors are scoped to one component.

type Box = { x: number; y: number; w: number; h: number };
type Pose = { assembly: number; time: number };
type ItemGeometry = {
  start: Box;
  album: Box;
  hasAlbum: boolean;
  timeline: Box | null;
  captionWidth: number;
  timelineCaptionWidth: number;
  visible: boolean;
  delay: number;
};
type Geometry = {
  album: Box;
  timeline: Box;
  items: Record<string, ItemGeometry>;
  albumBudget: string;
  solutionHeight: number;
  albumHeight: number;
  timelineHeight: number;
};

export type SceneMotion = {
  setStep: (step: number) => void;
  destroy: () => void;
};

export const clampStep = (value: unknown) =>
  Math.max(
    0,
    Math.min(2, Number.isFinite(Number(value)) ? Math.round(Number(value)) : 0),
  );
export const mixBox = (a: Box, b: Box, t: number): Box => ({
  x: a.x + (b.x - a.x) * t,
  y: a.y + (b.y - a.y) * t,
  w: a.w + (b.w - a.w) * t,
  h: a.h + (b.h - a.h) * t,
});
const clamp = (value: number) => Math.max(0, Math.min(1, value));
const ease = (value: number) => {
  const t = clamp(value);
  return t * t * (3 - 2 * t);
};
export const LOOSE_ROTATIONS: Record<string, number> = {
  summers: -4,
  postcard: 5,
  jump: -2,
  log: 4,
  video: -3,
  deed: -4,
  again: 5,
  audio: 3,
};

export function createSceneMotion(
  root: HTMLElement,
  initialStep = 0,
  {
    onPaint = () => {},
    onMeasure = () => {},
  }: {
    onPaint?: (position: number, pose: Pose) => void;
    onMeasure?: () => void;
  } = {},
): SceneMotion {
  const stage = root.querySelector<HTMLElement>('.gs-scene-stage')!;
  const album = root.querySelector<HTMLElement>(
    '[data-frame="album"] .vault-frame',
  )!;
  const timeline = root.querySelector<HTMLElement>(
    '[data-frame="timeline"] .vault-frame',
  )!;
  const shell = root.querySelector<HTMLElement>('.product-shell-carrier')!;
  const carriers = [...root.querySelectorAll<HTMLElement>('[data-carrier]')];
  const headings = [...root.querySelectorAll<HTMLElement>('[data-copy]')];
  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  let position = clampStep(initialStep);
  let target = position;
  let startPosition = position;
  let startTime: number | null = null;
  let raf = 0;
  let disposed = false;
  let geometry: Geometry | null = null;
  let lastWidth = 0;
  const box = (element: Element): Box => {
    const r = element.getBoundingClientRect();
    const s = stage.getBoundingClientRect();
    // Rects include the host's uniform fit transform; carriers use logical pixels.
    const scale = stage.clientWidth ? s.width / stage.clientWidth : 1;
    const factor = scale > 0 ? scale : 1;
    return {
      x: (r.left - s.left) / factor,
      y: (r.top - s.top) / factor,
      w: r.width / factor,
      h: r.height / factor,
    };
  };
  const selector = (key: string) =>
    key === 'jump' ? '.photo-slot' : `[data-media="${key}"]`;
  const paintCarrier = (
    element: HTMLElement,
    pose: Box | null,
    rotation: number,
    caption: number,
    width: number,
  ) => {
    if (!pose || pose.w <= 0 || pose.h <= 0) {
      element.style.opacity = '0';
      return;
    }
    const scale = pose.w / 1000;
    const height = pose.h / scale;
    element.style.height = `${height}px`;
    element.style.transform = `translate3d(${pose.x + pose.w / 2}px,${pose.y + pose.h / 2}px,0) rotate(${rotation}deg) scale(${scale}) translate(-500px,${-height / 2}px)`;
    element.style.setProperty('--media-height', `${height}px`);
    element.style.setProperty('--media-scale', String(scale));
    element.style.setProperty(
      '--media-radius',
      `${6 / Math.max(scale, 0.01)}px`,
    );
    element.style.setProperty('--caption-opacity', String(caption));
    element.style.setProperty('--caption-width', `${width}px`);
  };
  function paint(p: number) {
    if (!geometry || disposed) return;
    position = p;
    const assembly = ease(p);
    const time = ease(p - 1);
    const frame = mixBox(geometry.album, geometry.timeline, time);
    root.style.setProperty('--assembled', String(assembly));
    root.style.setProperty('--assembly-progress', String(assembly));
    root.style.setProperty('--timeline-progress', String(time));
    root.style.setProperty('--context-opacity', String(assembly * (1 - time)));
    stage.style.height = `${
      p < 1
        ? geometry.solutionHeight +
          (geometry.albumHeight - geometry.solutionHeight) * ease(p)
        : geometry.albumHeight +
          (geometry.timelineHeight - geometry.albumHeight) * time
    }px`;
    shell.style.width = `${frame.w}px`;
    shell.style.height = `${frame.h}px`;
    shell.style.transform = `translate3d(${frame.x}px,${frame.y}px,0)`;
    shell.style.opacity = String(ease((p - 0.28) / 0.55));
    // Preserve the original image sizing budget, independent of the fitted shell.
    shell.style.setProperty('--vault-current-height', geometry.albumBudget);
    shell.classList.toggle('is-timeline', time > 0.5);
    headings.forEach((h) => {
      const isSolution = h.dataset.copy === '0';
      h.style.opacity = String(
        isSolution ? 1 - ease(p * 1.6) : ease((p - 1.05) / 0.65),
      );
      h.setAttribute(
        'aria-hidden',
        String(clampStep(target) !== Number(h.dataset.copy)),
      );
    });
    carriers.forEach((element) => {
      const key = element.dataset.carrier ?? '';
      const g = geometry!.items[key];
      if (!g) return;
      const travel = p < 1 ? ease((p - g.delay) / (1 - g.delay)) : 1;
      let pose: Box | null;
      if (p < 1) pose = g.hasAlbum ? mixBox(g.start, g.album, travel) : g.start;
      else if (g.hasAlbum) pose = mixBox(g.album, g.timeline || g.album, time);
      else pose = g.timeline || g.start;
      const caption = p < 1 ? ease((p - 0.72) / 0.28) : 1;
      const width =
        g.captionWidth + (g.timelineCaptionWidth - g.captionWidth) * time;
      let opacity: number;
      if (g.hasAlbum)
        opacity = (g.visible ? 1 : ease(p)) * (g.timeline ? 1 : 1 - time);
      else if (p < 1) opacity = (g.visible ? 1 : 0) * (1 - assembly);
      else opacity = g.timeline ? time : 0;
      element.style.opacity = String(opacity);
      paintCarrier(
        element,
        pose,
        (LOOSE_ROTATIONS[key] || 0) * (1 - travel),
        caption,
        width,
      );
    });
    const reduced = preference.matches;
    root.classList.toggle('is-animated', !reduced);
    root.classList.toggle('is-static', reduced);
    root
      .querySelectorAll<HTMLElement>('[data-frame]')
      .forEach((anchor) =>
        anchor.setAttribute(
          'aria-hidden',
          String(
            !reduced || (anchor.dataset.frame === 'album' ? 1 : 2) !== target,
          ),
        ),
      );
    root.querySelector<HTMLElement>('.gs-solution-layout')!.style.opacity =
      String(reduced && target === 0 ? 1 : 0);
    root.querySelector<HTMLElement>('[data-frame="album"]')!.style.visibility =
      reduced && target === 1 ? 'visible' : 'hidden';
    root.querySelector<HTMLElement>(
      '[data-frame="timeline"]',
    )!.style.visibility = reduced && target === 2 ? 'visible' : 'hidden';
    // The host camera uses this exact pose in the same frame as the carriers.
    onPaint(p, { assembly, time });
  }
  const hasSize = (element: Element | null) => {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  };
  function measure() {
    if (disposed) return;
    root.classList.toggle('is-animated', !preference.matches);
    root.classList.toggle('is-static', preference.matches);
    const a = box(album);
    const t = box(timeline);
    const width = stage.clientWidth;
    const items: Record<string, ItemGeometry> = {};
    carriers.forEach((element, index) => {
      const key = element.dataset.carrier ?? '';
      const loose = root.querySelector<HTMLElement>(`[data-loose="${key}"]`)!;
      const am = album.querySelector(selector(key))!;
      const tm = timeline.querySelector(selector(key));
      const cap = album.querySelector(`[data-tile="${key}"] figcaption`)!;
      const tcap = timeline.querySelector(`[data-tile="${key}"] figcaption`);
      items[key] = {
        start: box(loose.querySelector('.vault-media,.photo-slot')!),
        album: box(am),
        hasAlbum: hasSize(am),
        timeline: tm && hasSize(tm) ? box(tm) : null,
        captionWidth: box(cap).w,
        timelineCaptionWidth: tcap ? box(tcap).w : box(cap).w,
        visible: getComputedStyle(loose).visibility !== 'hidden',
        delay: index * 0.025,
      };
    });
    const selected = box(
      timeline.querySelector('.vault-tile-jump .photo-slot')!,
    );
    const tile = box(timeline.querySelector('.vault-tile-jump')!);
    const selection = shell.querySelector<HTMLElement>('.timeline-selection')!;
    selection.style.left = `${selected.x - t.x - 10}px`;
    selection.style.top = `${selected.y - t.y - 8}px`;
    selection.style.width = `${selected.w + 20}px`;
    selection.style.height = `${tile.y + tile.h - selected.y + 16}px`;
    geometry = {
      album: a,
      timeline: t,
      items,
      albumBudget: getComputedStyle(album).getPropertyValue(
        '--vault-current-height',
      ),
      solutionHeight:
        Number(
          getComputedStyle(root)
            .getPropertyValue('--solution-height')
            .trim()
            .replace('px', ''),
        ) || 760,
      albumHeight: a.y + a.h + 24,
      timelineHeight: t.y + t.h + 24,
    };
    lastWidth = width;
    onMeasure();
    paint(preference.matches ? target : position);
  }
  function tick(timestamp: number) {
    if (disposed) return;
    if (startTime === null) startTime = timestamp;
    const progress = clamp((timestamp - startTime) / 1050);
    paint(startPosition + (target - startPosition) * ease(progress));
    if (progress < 1) raf = requestAnimationFrame(tick);
    else {
      raf = 0;
      paint(target);
    }
  }
  function setStep(next: number) {
    if (disposed) return;
    target = clampStep(next);
    cancelAnimationFrame(raf);
    raf = 0;
    if (preference.matches) {
      paint(target);
      return;
    }
    startPosition = position;
    startTime = null;
    if (startPosition === target) {
      paint(target);
      return;
    }
    raf = requestAnimationFrame(tick);
  }
  const resize = () => measure();
  const motionChanged = () => {
    cancelAnimationFrame(raf);
    raf = 0;
    measure();
    paint(target);
  };
  const observer =
    typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(() => {
          if (stage.clientWidth !== lastWidth) measure();
        });
  observer?.observe(root);
  window.addEventListener('resize', resize);
  preference.addEventListener('change', motionChanged);
  document.fonts?.addEventListener?.('loadingdone', measure);
  measure();
  document.fonts?.ready.then(() => {
    if (!disposed) measure();
  });
  return {
    setStep,
    destroy() {
      disposed = true;
      cancelAnimationFrame(raf);
      observer?.disconnect();
      window.removeEventListener('resize', resize);
      preference.removeEventListener('change', motionChanged);
      document.fonts?.removeEventListener?.('loadingdone', measure);
    },
  };
}
